import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import sortBy from "lodash/sortBy";
import { useOnPropagate } from "@/frontend/@api/PropagateProvider";
import { client } from "@/frontend/@api/trpcClient";
import { useFirmId } from "@/frontend/firm/FirmProvider";

const GET_FIRM_KEY = "getFirm"; // used by multiple hooks

function useFirmPropagation() {
    const key = GET_FIRM_KEY;

    useOnPropagate(key, p => {
        p.onSingleDefaults("firm", x => [key, x.id]);
    });
}

export const useFirm = (firmId: string) => {
    const key = GET_FIRM_KEY;

    const { data: firm } = useSuspenseQuery({
        queryKey: [key, firmId],
        queryFn: () => client.firm.getFirm.query({ firmId })
    });

    useFirmPropagation();

    return firm;
};

export const useCurrentFirm = () => {
    const { firmId } = useFirmId();
    return useFirm(firmId);
};

export const useFirms = () => {
    const key = "getAllFirms";

    const { data: rawFirms } = useSuspenseQuery({
        queryKey: [key],
        queryFn: () => client.firm.getAllFirms.query()
    });

    const firms = sortBy(rawFirms, x => x.name.toLocaleLowerCase());

    useOnPropagate(key, p => {
        p.onCollectionDefaults("firm", [key]);
    });

    return firms;
};

export const useMultipleFirms = (firmIds: string[]) => {
    const key = GET_FIRM_KEY;

    const result = useSuspenseQueries({
        queries: firmIds.map(id => ({
            queryKey: [key, id],
            queryFn: () => client.firm.getFirm.query({ firmId: id })
        }))
    });

    useFirmPropagation();

    return result.map(x => x.data);
};
// re-export for prefetching (react-compiler eslint doesn't like passing hooks as reference)
export const reexported__useFirm = useFirm;
export const reexported__useCurrentFirm = useCurrentFirm;
export const reexported__useMultipleFirms = useMultipleFirms;
